import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';
import { sanitizeIdentifier as sanitizeAlias, identifierFromFilename as aliasFromFilename } from '../../../utils/sanitizeIdentifier';

// ── Helpers ───────────────────────────────────────────────────────────────────

function newSource() {
  return { alias: '', sql: '', description: '', _id: Math.random().toString(36).slice(2) };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TestResultPanel({ result }) {
  if (!result) return null;
  if (!result.success) {
    return (
      <div className="mt-2 p-2 rounded bg-red-900/30 border border-red-700/50 text-xs text-red-300">
        {result.error || 'Query failed'}
      </div>
    );
  }
  const cols = result.columns || [];
  return (
    <div className="mt-2 space-y-1">
      <div className="text-xs text-zinc-400">
        {result.row_count?.toLocaleString() ?? '?'} rows &middot; {result.execution_time_ms?.toFixed(0)} ms
      </div>
      <div className="flex flex-wrap gap-1">
        {cols.map(col => (
          <span key={col} className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 text-xs border border-emerald-700/30">
            {col}
          </span>
        ))}
      </div>
    </div>
  );
}

function SourceRow({ source, index, onChange, onDelete, onTestQuery }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [expanded, setExpanded] = useState(!source.alias);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleTest = async () => {
    if (!source.sql.trim() || !onTestQuery) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await onTestQuery(source.sql);
      setTestResult(res);
    } catch (err) {
      setTestResult({ success: false, error: err?.response?.data?.detail ?? err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setUploadError(null);
    try {
      const text = await file.text();
      onChange({
        ...source,
        sql: text,
        alias: source.alias || aliasFromFilename(file.name),
      });
      setTestResult(null);
    } catch (err) {
      setUploadError(err?.message ?? 'Could not read file');
    }
  };

  return (
    <div className="border border-zinc-700/60 rounded-lg overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-2 px-3 py-2 bg-zinc-800/60 cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}
      >
        <svg
          className={`w-3.5 h-3.5 text-zinc-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
        </svg>

        <span className="text-xs font-mono font-medium text-zinc-200 flex-1 truncate">
          {source.alias || <span className="text-zinc-500 italic">unnamed source #{index + 1}</span>}
        </span>
        {source.description && (
          <span className="text-xs text-zinc-500 truncate max-w-48">{source.description}</span>
        )}

        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          className="p-0.5 rounded hover:bg-red-900/40 text-zinc-500 hover:text-red-400 flex-shrink-0"
          title="Remove source"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="p-3 space-y-3 bg-zinc-900/40">
          {/* Alias + Description row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">
                Alias <span className="text-zinc-600">(used as DuckDB table name)</span>
              </label>
              <input
                type="text"
                value={source.alias}
                onChange={e => onChange({ ...source, alias: sanitizeAlias(e.target.value) })}
                placeholder="e.g. zuege_data"
                className="w-full px-2 py-1.5 rounded bg-zinc-800 border border-zinc-600/60 text-xs font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500/60"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Description (optional)</label>
              <input
                type="text"
                value={source.description}
                onChange={e => onChange({ ...source, description: e.target.value })}
                placeholder="e.g. Machine cycle data from HMI"
                className="w-full px-2 py-1.5 rounded bg-zinc-800 border border-zinc-600/60 text-xs text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500/60"
              />
            </div>
          </div>

          {/* SQL editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs text-zinc-400">
                SQL Server Query
              </label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700/60"
                title="Upload a .txt or .sql file to fill this query"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Upload file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.sql,text/plain"
                onChange={handleFileSelected}
                className="hidden"
              />
            </div>
            <textarea
              value={source.sql}
              onChange={e => onChange({ ...source, sql: e.target.value })}
              rows={5}
              placeholder={'SELECT *\nFROM dbo.YourTable\nWHERE ...'}
              spellCheck={false}
              className="w-full px-2 py-1.5 rounded bg-zinc-800 border border-zinc-600/60 text-xs font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-blue-500/60 resize-y"
            />
            {uploadError && (
              <div className="mt-1 text-xs text-red-400">Could not read file: {uploadError}</div>
            )}
          </div>

          {/* Test button + result */}
          <div>
            <button
              onClick={handleTest}
              disabled={testing || !source.sql.trim()}
              className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600/80 hover:bg-blue-500/80 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {testing ? (
                <>
                  <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Testing...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                  </svg>
                  Test against SQL Server
                </>
              )}
            </button>
            <TestResultPanel result={testResult} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ExtractionSourcesConfig({ customerId, onSourcesSaved }) {
  const api = useMemo(() => (customerId ? createCustomerETLApi(customerId) : null), [customerId]);
  const [sources, setSources] = useState([]);
  const [savedSources, setSavedSources] = useState([]); // last persisted snapshot
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved' | 'error'
  const [saveError, setSaveError] = useState(null);
  const [fullConfig, setFullConfig] = useState(null); // kept to merge on save
  const newSourceFileInputRef = useRef(null);

  // Load on mount / when customer changes
  useEffect(() => {
    if (!api) return;
    setLoading(true);
    api.getTransformerConfig()
      .then(cfg => {
        setFullConfig(cfg);
        const loaded = (cfg.extraction_sources || []).map(s => ({
          ...s,
          _id: Math.random().toString(36).slice(2),
        }));
        setSources(loaded.length ? loaded : []);
        setSavedSources(loaded.length ? loaded : []);
      })
      .catch(() => setSources([]))
      .finally(() => setLoading(false));
  }, [api]);

  // True when local state differs from last saved snapshot
  const isDirty = JSON.stringify(sources.map(({ _id, ...s }) => s)) !==
    JSON.stringify(savedSources.map(({ _id, ...s }) => s));

  // Rows with a blank alias or SQL query — the backend rejects saving (from ANY
  // tab) while these exist, so surface them clearly and offer a one-click fix.
  const blankCount = sources.filter(s => !s.alias.trim() || !s.sql.trim()).length;

  const handleChange = useCallback((index, updated) => {
    setSources(prev => prev.map((s, i) => (i === index ? updated : s)));
  }, []);

  const handleDelete = useCallback((index) => {
    setSources(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveBlank = useCallback(() => {
    setSources(prev => prev.filter(s => s.alias.trim() && s.sql.trim()));
  }, []);

  const handleAdd = () => {
    setSources(prev => [...prev, newSource()]);
  };

  const handleUploadNew = async (e) => {
    const fileList = Array.from(e.target.files || []);
    e.target.value = '';
    if (!fileList.length) return;

    const takenAliases = new Set(sources.map(s => s.alias).filter(Boolean));
    const newRows = [];

    for (const file of fileList) {
      let text;
      try {
        text = await file.text();
      } catch (err) {
        continue; // skip files that can't be read as text
      }
      const base = aliasFromFilename(file.name) || 'unnamed_source';
      let alias = base;
      let suffix = 2;
      while (takenAliases.has(alias)) {
        alias = `${base}_${suffix}`;
        suffix += 1;
      }
      takenAliases.add(alias);
      newRows.push({ ...newSource(), alias, sql: text });
    }

    if (newRows.length) {
      setSources(prev => [...prev, ...newRows]);
    }
  };

  const handleSave = async () => {
    const blankIndex = sources.findIndex(s => !s.alias.trim() || !s.sql.trim());
    if (blankIndex !== -1) {
      setSaveStatus('error');
      setSaveError(
        `"${sources[blankIndex].alias || `unnamed source #${blankIndex + 1}`}" is missing an alias or SQL query — fill in both fields or remove the row.`,
      );
      return;
    }
    setSaving(true);
    setSaveStatus(null);
    setSaveError(null);
    try {
      const payload = {
        // Keep existing sources/ocel_source_mappings intact — only update extraction_sources
        sources: fullConfig?.sources ?? {},
        ocel_source_mappings: fullConfig?.ocel_source_mappings ?? {},
        extraction_sources: sources.map(({ _id, ...rest }) => rest),
      };
      await api.saveTransformerConfig(payload);
      // Refresh full config so subsequent saves stay in sync
      const refreshed = await api.getTransformerConfig();
      setFullConfig(refreshed);
      setSavedSources([...sources]);
      setSaveStatus('saved');
      onSourcesSaved?.();
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail ?? err?.message ?? 'Unknown error';
      console.error('ExtractionSourcesConfig save failed:', detail, err?.response?.data);
      setSaveStatus('error');
      setSaveError(detail);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-zinc-500 text-sm">
        Loading configuration...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-100">Extraction Sources</h3>
        <p className="text-xs text-zinc-400 mt-1">
          Define named SQL queries that pull data from the external SQL Server database.
          Each query is stored in DuckDB under its alias and can be referenced in the OCEL Config tab.
        </p>
      </div>

      {blankCount > 0 && (
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-amber-900/20 border border-amber-700/40 text-xs text-amber-300">
          <span>
            {blankCount} source{blankCount !== 1 ? 's' : ''} {blankCount !== 1 ? 'have' : 'has'} a blank alias or SQL query
            below and will block saving until fixed or removed.
          </span>
          <button
            onClick={handleRemoveBlank}
            className="flex-shrink-0 px-2 py-1 rounded text-[11px] font-medium bg-amber-800/50 hover:bg-amber-700/60 text-amber-100"
          >
            Remove blank row{blankCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Source rows */}
      {sources.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 border border-dashed border-zinc-700/60 rounded-lg text-zinc-500">
          <svg className="w-8 h-8 mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375" />
          </svg>
          <p className="text-sm">No extraction sources defined</p>
          <p className="text-xs mt-1">Add a source to define which SQL Server data to extract.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((src, i) => (
            <SourceRow
              key={src._id}
              source={src}
              index={i}
              onChange={updated => handleChange(i, updated)}
              onDelete={() => handleDelete(i)}
              onTestQuery={api ? (sql) => api.executeERPQuery(sql) : null}
            />
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleAdd}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border border-zinc-600/60 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Source
        </button>

        <button
          onClick={() => newSourceFileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border border-zinc-600/60 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
          title="Create new sources from one or more .txt or .sql files"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Upload Source File(s)
        </button>
        <input
          ref={newSourceFileInputRef}
          type="file"
          accept=".txt,.sql,text/plain"
          onChange={handleUploadNew}
          multiple
          className="hidden"
        />

        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium disabled:opacity-40 transition-colors ${
            isDirty
              ? 'bg-amber-500 hover:bg-amber-400 text-white ring-2 ring-amber-400/40'
              : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-100'
          }`}
        >
          {saving ? (
            <>
              <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Saving...
            </>
          ) : 'Save'}
        </button>

        {isDirty && !saving && saveStatus !== 'saved' && (
          <span className="text-xs text-amber-400">Unsaved changes</span>
        )}

        {saveStatus === 'saved' && (
          <span className="text-xs text-emerald-400 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
            </svg>
            Saved
          </span>
        )}
        {saveStatus === 'error' && (
          <span className="text-xs text-red-400" title={saveError ?? ''}>
            Save failed{saveError ? ` — ${saveError}` : ''}
          </span>
        )}
      </div>

      {/* Info note */}
      {sources.length > 0 && (
        <div className="text-xs text-zinc-500 border-t border-zinc-800 pt-3">
          After saving, run <span className="font-mono text-zinc-400">Step 1 (Extract)</span> in the Pipeline tab to pull data into DuckDB.
          Then map these sources to OCEL inputs in the <span className="font-mono text-zinc-400">OCEL Config</span> tab.
        </div>
      )}
    </div>
  );
}
